import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import Select from "react-select";

import { useToast } from "src/context/ToastContext";
import { CriteriaManifestationEntity } from "src/entities/CriteriaManifestationEntity";
import { CriteriaRecomendationEntity } from "src/entities/CriteriaRecomendationEntity";
import CriteriaManifestationService, { criteriaManifestationQueryKeys } from "src/services/CriteriaManifestationService";
import CriteriaRecomendationService, { criteriaRecomendationQueryKeys } from "src/services/CriteriaRecomendationService";
import { SelectOption, SelectOptions, SelectStyle } from "src/config/select";
import Navigation from "./Navigation";
import { clearCriteriaStepDraft, getCriteriaStepDraft, setCriteriaStepDraft } from "./draftStorage";

const criteriaManifestationService = new CriteriaManifestationService();
const criteriaRecomendationService = new CriteriaRecomendationService();

type RecomendationItem = {
	id?: number;
	title: string;
	description: string;
	manifestations: number[];
	originalTitle?: string;
	originalDescription?: string;
	originalManifestations?: number[];
};

type StepRecomendationsDraft = {
	recomendations: RecomendationItem[];
	removedIds: number[];
};

type StepRecomendationsProps = {
	criteriaId: number;
	backLabel: string;
	nextLabel: string;
	onBack: () => void;
	onComplete: () => void;
};

export default function StepRecomendations({ criteriaId, backLabel, nextLabel, onBack, onComplete }: StepRecomendationsProps) {
	const { handleToast } = useToast();
	const draftRef = useRef(getCriteriaStepDraft<StepRecomendationsDraft>(criteriaId, "recomendations"));
	const [recomendations, setRecomendations] = useState<RecomendationItem[]>(() => draftRef.current?.recomendations ?? []);
	const [removedIds, setRemovedIds] = useState<number[]>(() => draftRef.current?.removedIds ?? []);

	const criteriaIdStr = String(criteriaId);

	const { data: manifestationsData, isLoading: manifestationsLoading } = useQuery<CriteriaManifestationEntity[]>(
		criteriaManifestationQueryKeys.manifestations(criteriaIdStr),
		() => criteriaManifestationService.getManifestations(criteriaId).then(response => response.data.manifestations),
		{ refetchOnWindowFocus: false }
	);

	const { data: recomendationsData, isLoading: recomendationsLoading } = useQuery<CriteriaRecomendationEntity[]>(
		criteriaRecomendationQueryKeys.recomendations(criteriaIdStr),
		() => criteriaRecomendationService.getRecomendations(criteriaId).then(response => response.data.recomendations),
		{ refetchOnWindowFocus: false }
	);

	useEffect(() => {
		if (!draftRef.current && recomendationsData) {
			setRecomendations(recomendationsData.map(recomendation => {
				const manifestationIds = recomendation.manifestations.map(manifestation => manifestation.id);

				return {
					id: recomendation.id,
					title: recomendation.title,
					description: recomendation.description,
					manifestations: manifestationIds,
					originalTitle: recomendation.title,
					originalDescription: recomendation.description,
					originalManifestations: manifestationIds,
				};
			}));
		}
	}, [recomendationsData]);

	const manifestationOptions = manifestationsData?.map(manifestation => ({ label: manifestation.title, value: manifestation.id })) ?? [];
	const isLoading = manifestationsLoading || recomendationsLoading;

	async function saveDeleted() {
		await Promise.all(removedIds.map(recomendationId => criteriaRecomendationService.deleteRecomendationRelation(criteriaId, recomendationId)));
	}

	async function saveUpdated(updated: RecomendationItem[]) {
		await Promise.all(updated.map(recomendation => criteriaRecomendationService.putRecomendation({ id: recomendation.id!, title: recomendation.title, description: recomendation.description })));
	}

	async function syncManifestations(existing: RecomendationItem[]) {
		const calls: Promise<unknown>[] = [];

		for (const recomendation of existing) {
			const original = recomendation.originalManifestations ?? [];
			const added = recomendation.manifestations.filter(id => !original.includes(id));
			const removed = original.filter(id => !recomendation.manifestations.includes(id));

			if (added.length > 0) {
				calls.push(criteriaRecomendationService.postRecomendationManifestationRelations({ recomendationId: recomendation.id!, manifestations: added }));
			}

			for (const manifestationId of removed) {
				calls.push(criteriaRecomendationService.deleteRecomendationManifestationRelation(recomendation.id!, manifestationId));
			}
		}

		await Promise.all(calls);
	}

	async function saveCreated(created: RecomendationItem[]) {
		if (created.length > 0) {
			const responses = await Promise.all(
				created.map(recomendation => criteriaRecomendationService.postRecomendation({ title: recomendation.title, description: recomendation.description }))
			);

			const newIds = responses.map(response => response.data.id);
			const calls: Promise<unknown>[] = [
				criteriaRecomendationService.postRecomendationRelations({ criteriaId, recomendations: newIds })
			];

			created.forEach((recomendation, idx) => {
				if (recomendation.manifestations.length > 0) {
					calls.push(criteriaRecomendationService.postRecomendationManifestationRelations({ recomendationId: newIds[idx], manifestations: recomendation.manifestations }));
				}
			});

			await Promise.all(calls);
		}
	}

	const mutation = useMutation(
		async () => {
			const created = recomendations.filter(recomendation => !recomendation.id && recomendation.title.trim());
			const updated = recomendations.filter(recomendation => recomendation.id && (recomendation.title !== recomendation.originalTitle || recomendation.description !== recomendation.originalDescription));
			const existing = recomendations.filter(recomendation => recomendation.id);

			await Promise.all([
				saveDeleted(),
				saveUpdated(updated),
				syncManifestations(existing),
				saveCreated(created),
			]);
		},
		{
			onSuccess: () => {
				clearCriteriaStepDraft(criteriaId, "recomendations");
				onComplete();
			},
			onError: (err: AxiosError) => {
				handleToast("Critério", `Problema na operação: ${err.message}`, 5000, "danger");
			},
		}
	);

	function handleNext() {
		if (recomendations.length === 0) {
			handleToast("Critério", "É necessário adicionar pelo menos uma recomendação para continuar.", 5000, "danger");
			return;
		}
		mutation.mutate();
	}

	function handleAdd() {
		setRecomendations(prev => [...prev, { title: "", description: "", manifestations: [] }]);
	}

	function handleTitleChange(index: number, value: string) {
		setRecomendations(prev => prev.map((recomendation, idx) => idx === index ? { ...recomendation, title: value } : recomendation));
	}

	function handleDescriptionChange(index: number, value: string) {
		setRecomendations(prev => prev.map((recomendation, idx) => idx === index ? { ...recomendation, description: value } : recomendation));
	}

	function handleManifestationsChange(index: number, value: readonly SelectOption[] | null) {
		setRecomendations(prev => prev.map((recomendation, idx) => idx === index ? { ...recomendation, manifestations: value ? value.map(option => option.value) : [] } : recomendation));
	}

	function handleRemove(index: number) {
		const recomendation = recomendations[index];
		
		if (recomendation.id) {
			setRemovedIds(prev => [...prev, recomendation.id!]);
		}

		setRecomendations(prev => prev.filter((_, idx) => idx !== index));
	}

	function handleBack() {
		setCriteriaStepDraft(criteriaId, "recomendations", { recomendations, removedIds });
		onBack();
	}

	return (
		<>
			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Recomendações
				</Card.Header>
				<Card.Body>
					{isLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<div key={skeletonIndex} className="mb-3">
								<Row className="mb-2 gy-2">
									<Col md={6}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col md={6}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
								</Row>
								<Row className="align-items-end gy-2">
									<Col xs={12} lg>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col xs={12} lg="auto" className="d-flex align-items-end">
										<div className="skeleton skeleton-button" style={{ height: 38, width: 44 }} />
									</Col>
								</Row>
							</div>
						))
					) : recomendations.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						recomendations.map((recomendation, index) => (
							<div
								key={index}
								className={index < recomendations.length - 1 ? "border-bottom pb-4 mb-3" : "mb-3"}
							>
								<Row className="mb-2 gy-2">
									<Col md={6}>
										<Form.Label className="text-muted"><small>Título *</small></Form.Label>
										<Form.Control
											type="text"
											placeholder="Informe aqui"
											value={recomendation.title}
											onChange={(e) => handleTitleChange(index, e.target.value)}
										/>
									</Col>
									<Col md={6}>
										<Form.Label className="text-muted"><small>Descrição *</small></Form.Label>
										<Form.Control
											type="text"
											placeholder="Informe aqui"
											value={recomendation.description}
											onChange={(e) => handleDescriptionChange(index, e.target.value)}
										/>
									</Col>
								</Row>
								<Row className="align-items-end gy-2">
									<Col xs={12} lg>
										<Form.Label className="text-muted"><small>Manifestações</small></Form.Label>
										<Select
											placeholder="Selecione"
											isMulti
											options={manifestationOptions}
											value={manifestationOptions.filter(opt => recomendation.manifestations.includes(opt.value))}
											onChange={(value) => handleManifestationsChange(index, value)}
											styles={SelectStyle}
											{...SelectOptions}
										/>
									</Col>
									<Col xs={12} lg="auto" className="d-flex align-items-end">
										<Button variant="light" className="rounded-3 shadow-sm" type="button" onClick={() => handleRemove(index)}>
											<Icon path={mdiTrashCanOutline} size={1} className="text-danger" />
										</Button>
									</Col>
								</Row>
							</div>
						))
					)}
				</Card.Body>
				<Card.Footer className="bg-white">
					<Button
						variant="light"
						className="d-flex rounded-3 shadow-sm"
						type="button"
						onClick={handleAdd}
						disabled={isLoading}
					>
						<Icon path={mdiPlus} size={1} /> Adicionar Recomendação
					</Button>
				</Card.Footer>
			</Card>
			<Navigation
				backLabel={backLabel}
				nextLabel={nextLabel}
				saving={mutation.isLoading}
				onBack={handleBack}
				onNext={handleNext}
			/>
		</>
	);
}
